import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AlertTriangle, BadgeCheck, Globe, IdCard, UserPlus, RefreshCw, Send, StickyNote, Trash2, UserX } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { CountrySelect } from '@/components/CountrySelect';
import { countryFlagClass, getCountryName } from '@/lib/countries';
import { updatePlayer } from '@/lib/api';
import type { Player } from '@swiss-magic/shared-types';
import type { PlayersController } from '@/pages/players/usePlayersController';
import { playerDisplayName } from './player-utils';
import { PlayerStatusBadge } from './PlayerStatusBadge';
import { StatusToggle } from './StatusToggle';

interface PlayerRowProps {
  player: Player;
  playerIndex: number;
  ctrl: PlayersController;
  tournamentId: string;
}

export function PlayerRow({ player, playerIndex, ctrl, tournamentId }: PlayerRowProps) {
  const { t, i18n } = useTranslation('director');
  const {
    setPlayers,
    showRating,
    showExternalId,
    canEdit,
    isRegistrationOpen,
    canWithdrawPlayers,
    editingCell,
    setEditingCell,
    editValue,
    setEditValue,
    editInputRef,
    isPlayerDuplicate,
    startEdit,
    commitEdit,
    handleEditKeyDown,
    handleStatusChange,
    handleWithdraw,
    requestRemovePlayer,
    setNotesTarget,
    setNotesText,
    openInviteDialog,
    openProfileViewer,
    handleRefreshFromProfile,
    handleUnblockPlayer,
    messagePlayer,
    setSelectedPlayer,
    handleSecureSpot,
  } = ctrl;

  const displayName = playerDisplayName(player);
  const isDupe = isPlayerDuplicate(player);
  const isWithdrawn =
    player.status === 'withdrawn' || player.status === 'eliminated';
  const isSelfEnrolled = player.profileUserId != null;
  const PROFILE_FIELDS = new Set([
    'firstName',
    'lastName',
    'rating',
    'fideId',
    'city',
    'country',
    'club',
  ]);

  function renderCell(field: string, value: string, testId: string, width: string) {
    const isReadOnly = isSelfEnrolled && PROFILE_FIELDS.has(field);
    if (isReadOnly) {
      return (
        <td
          className={`${width} px-2 py-1`}
          data-testid={testId}
          data-readonly="true"
        >
          <span className="text-sm text-muted-foreground">
            {field === 'country' && value ? (
              <>
                <span className={countryFlagClass(value)} /> {getCountryName(value, i18n.language)}
              </>
            ) : (
              value
            )}
          </span>
        </td>
      );
    }

    const isEditing =
      editingCell?.playerId === player.id && editingCell?.field === field;

    if (isEditing) {
      if (field === 'country') {
        return (
          <td className={width}>
            <CountrySelect
              data-testid={`${testId}-edit`}
              value={editValue}
              onChange={(code) => {
                setEditValue(code);
                const val = code || null;
                if (val !== (player.country ?? '')) {
                  updatePlayer(tournamentId, player.id, { country: val })
                    .then((updated) => {
                      setPlayers((prev) =>
                        prev.map((p) => (p.id === updated.id ? updated : p)),
                      );
                    })
                    .catch(() => {});
                }
                setEditingCell(null);
              }}
            />
          </td>
        );
      }
      return (
        <td className={width}>
          <Input
            ref={editInputRef}
            data-testid={`${testId}-edit`}
            className="h-7 text-sm"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={commitEdit}
            onKeyDown={handleEditKeyDown}
          />
        </td>
      );
    }

    return (
      <td
        className={`${width} cursor-pointer px-2 py-1`}
        data-testid={testId}
        onClick={() => canEdit && startEdit(player.id, field, value)}
      >
        <span className="text-sm">
          {field === 'country' && value ? (
            <>
              <span className={countryFlagClass(value)} /> {getCountryName(value, i18n.language)}
            </>
          ) : (
            value
          )}
        </span>
      </td>
    );
  }

  return (
    <tr
      data-testid={`player-row-${displayName}`}
      className={`border-b hover:bg-muted/30 ${
        isDupe ? 'bg-yellow-50' : ''
      } ${isWithdrawn ? 'opacity-50' : ''}`}
    >
      <td className="w-8 px-2 py-1 text-xs text-muted-foreground">
        {playerIndex + 1}
      </td>
      <td className="w-6 px-1 py-1">
        {isDupe && (
          <span
            title={t('players_page.duplicate_title')}
            data-testid={`duplicate-flag-${displayName}`}
          >
            <AlertTriangle className="h-4 w-4 text-yellow-600" />
          </span>
        )}
        {isSelfEnrolled && (
          <Button
            variant="outline"
            size="sm"
            title={t('players_page.self_enrolled_title')}
            aria-label={t('players_page.self_enrolled_aria', { name: displayName })}
            data-testid={`self-enrolled-indicator-${displayName}`}
            onClick={() => openProfileViewer(player)}
            className="h-7 w-7 p-0"
          >
            <Globe className="h-4 w-4" />
          </Button>
        )}
      </td>
      {renderCell('firstName', player.firstName, `player-firstName-${displayName}`, 'w-32')}
      {renderCell('lastName', player.lastName, `player-lastName-${displayName}`, 'w-32')}
      {showRating &&
        renderCell(
          'rating',
          player.rating != null ? String(player.rating) : '',
          `player-rating-${displayName}`,
          'w-20',
        )}
      {showExternalId &&
        renderCell(
          'fideId',
          player.fideId != null ? String(player.fideId) : '',
          `player-fideId-${displayName}`,
          'w-24',
        )}
      {renderCell('city', player.city ?? '', `player-city-${displayName}`, 'w-24')}
      {renderCell('country', player.country ?? '', `player-country-${displayName}`, 'w-24')}
      {renderCell('club', player.club ?? '', `player-club-${displayName}`, 'w-28')}
      <td className="w-52 px-2 py-1">
        {canEdit && !isWithdrawn && player.status !== 'blocked' ? (
          <StatusToggle
            showBadge
            status={player.status}
            onChange={(s) => handleStatusChange(player, s)}
            testIdPrefix={`player-${displayName}`}
          />
        ) : (
          <div className="flex items-center gap-2">
            <span
              className="text-xs"
              data-testid={`player-${displayName}-status-badge`}
            >
              <PlayerStatusBadge status={player.status} />
            </span>
            {canEdit && player.status === 'blocked' && (
              <Button
                variant="outline"
                size="sm"
                className="h-6 px-2 text-xs"
                data-testid={`player-${displayName}-unblock`}
                onClick={() => handleUnblockPlayer(player)}
              >
                {t('players_page.unblock_button')}
              </Button>
            )}
          </div>
        )}
      </td>
      <td className="w-24 px-2 py-1">
        <div className="flex items-center gap-1">
          {/* Story 100 — secure a pending/waitlisted player's spot. */}
          {canEdit &&
            (player.status === 'none' || player.status === 'waitlisted') && (
              <Button
                variant="ghost"
                size="sm"
                data-testid={`secure-spot-${displayName}`}
                className="h-7 w-7 p-0"
                title={t('players_page.secure_spot_title')}
                onClick={() => handleSecureSpot(player)}
              >
                <BadgeCheck className="h-4 w-4 text-sky-600" />
              </Button>
            )}
          {/* Story 096 — open the full registration card (same panel as
              mobile) so all per-player fields, incl. FIDE details, are
              editable on desktop. */}
          <Button
            variant="ghost"
            size="sm"
            data-testid={`player-details-${displayName}`}
            className="h-7 w-7 p-0"
            title={t('players_page.detail_panel.open_details_title')}
            onClick={() => setSelectedPlayer(player)}
          >
            <IdCard className="h-4 w-4 text-muted-foreground" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            data-testid={`player-notes-${displayName}`}
            className="h-7 w-7 p-0"
            onClick={() => {
              setNotesTarget(player);
              setNotesText(player.notes ?? '');
            }}
          >
            <StickyNote
              className={`h-4 w-4 ${
                player.notes ? 'text-yellow-600' : 'text-muted-foreground'
              }`}
            />
          </Button>
          {!isSelfEnrolled && (
            <Button
              variant="ghost"
              size="sm"
              data-testid={`invite-player-${displayName}`}
              className="h-7 w-7 p-0"
              title={t('players_page.detail_panel.invite_player_title')}
              onClick={() => openInviteDialog(player)}
            >
              <UserPlus className="h-4 w-4 text-muted-foreground" />
            </Button>
          )}
          {isSelfEnrolled && (
            <Button
              variant="ghost"
              size="sm"
              data-testid={`refresh-player-${displayName}`}
              className="h-7 w-7 p-0"
              title={t('players_page.detail_panel.refresh_title')}
              onClick={() => handleRefreshFromProfile(player)}
            >
              <RefreshCw className="h-4 w-4 text-muted-foreground" />
            </Button>
          )}
          {isSelfEnrolled && (
            <Button
              variant="ghost"
              size="sm"
              data-testid={`message-player-${displayName}`}
              className="h-7 w-7 p-0"
              title={t('players_page.detail_panel.message_player_title')}
              onClick={() => messagePlayer(player)}
            >
              <Send className="h-4 w-4 text-muted-foreground" />
            </Button>
          )}
        </div>
      </td>
      {(isRegistrationOpen || canWithdrawPlayers) && (
        <td className="w-10 px-2 py-1">
          {canWithdrawPlayers && player.status === 'accepted' && (
            <Button
              variant="destructive"
              size="sm"
              className="h-7 px-2 text-xs"
              data-testid={`withdraw-player-${displayName}`}
              onClick={() => handleWithdraw(player)}
            >
              <UserX className="mr-1 h-3.5 w-3.5" />
              {t('players_page.withdraw_button')}
            </Button>
          )}
          {isRegistrationOpen && !isWithdrawn && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0"
              data-testid={`remove-player-${displayName}`}
              onClick={() => requestRemovePlayer(player)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </td>
      )}
    </tr>
  );
}
